import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
  Box,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import Iconify from '../components/iconify';
import * as docsService from '../services/docsService';
import { errorNotification } from '../services/notificationService';

export default function HelpPage() {
  const [manifest, setManifest] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleContent, setArticleContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [articleLoading, setArticleLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await docsService.getManifest();
        setManifest(typeof data === 'string' ? JSON.parse(data) : data);
      } catch (error) {
        errorNotification('Failed to load help articles');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openArticleBySlug = useCallback(async (slug) => {
    const article = manifest?.articles?.find((a) => a.id === slug);
    if (!article) return;
    try {
      setArticleLoading(true);
      setSelectedArticle(article);
      const content = await docsService.getArticle(slug);
      setArticleContent(content);
    } catch (error) {
      errorNotification('Failed to load article');
      setSelectedArticle(null);
    } finally {
      setArticleLoading(false);
    }
  }, [manifest]);

  const openArticle = useCallback((article) => {
    openArticleBySlug(article.id);
  }, [openArticleBySlug]);

  const handleDownloadWorkbook = async () => {
    try {
      setDownloading(true);
      await docsService.downloadWorkbook();
    } catch (error) {
      errorNotification('Failed to download workbook');
    } finally {
      setDownloading(false);
    }
  };

  const goBack = () => {
    setSelectedArticle(null);
    setArticleContent('');
  };

  // Group articles by category
  const categories = {};
  if (manifest?.articles) {
    manifest.articles.forEach((article) => {
      const cat = article.category || 'General';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(article);
    });
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Article detail view
  if (selectedArticle) {
    return (
      <>
        <Helmet>
          <title>{selectedArticle.title} | Help</title>
        </Helmet>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <IconButton onClick={goBack} sx={{ mr: 1 }}>
              <Iconify icon="eva:arrow-back-fill" />
            </IconButton>
            <Typography variant="h4">{selectedArticle.title}</Typography>
          </Stack>
          {articleLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Card>
              <CardContent sx={{ '& img': { maxWidth: '100%' }, '& h2': { mt: 3 }, '& p': { lineHeight: 1.8 } }}>
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => {
                      if (href?.endsWith('.md')) {
                        const slug = href.replace(/\.md$/, '');
                        return (
                          <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); openArticleBySlug(slug); }}
                            style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            {children}
                          </a>
                        );
                      }
                      return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                    },
                  }}
                >
                  {articleContent}
                </ReactMarkdown>
              </CardContent>
            </Card>
          )}
        </Container>
      </>
    );
  }

  // Article list view
  return (
    <>
      <Helmet>
        <title>Help & Guides</title>
      </Helmet>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 4 }}>
          Help & Guides
        </Typography>

        <Card sx={{ mb: 4 }}>
          <CardActionArea onClick={handleDownloadWorkbook} disabled={downloading}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify icon="eva:file-text-outline" width={36} sx={{ color: 'primary.main' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">Download Analysis Workbook</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Excel workbook for analyzing exported sensor CSV data
                  </Typography>
                </Box>
                {downloading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Iconify icon="eva:download-outline" width={24} />
                )}
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>

        {Object.entries(categories).map(([category, articles]) => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
              {category}
            </Typography>
            <Grid container spacing={2}>
              {articles.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Card>
                    <CardActionArea onClick={() => openArticle(article)}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          {article.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {article.summary}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {article.tags?.slice(0, 3).map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Container>
    </>
  );
}
